import {
  Grid,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Link,
  Chip,
  useMediaQuery,
  Container,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EditIcon from "@mui/icons-material/Edit";
import { Modal } from "../common/Modal";
import ProfileOverviewEditForm from "./ProfileOverviewEditForm";
import { useUserAuth } from "../../context/UserAuthContext";
import theme from "../../styles/styleTheme";
import moment from "moment";
import StarsIcon from "@mui/icons-material/Stars";
import NAV from "../../utils/nav.const";
import StarIcon from "@mui/icons-material/Star";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/currency";
import { useNavigate } from "react-router-dom";

const ProfileOverview = () => {
  const { t } = useTranslation();
  const { user, reloadUserData } = useUserAuth();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [editModal, setEditModal] = useState<boolean>(false);
  const closeEditModal = () => setEditModal(!editModal);

  const profileInfo = [
    {
      label: t("profile.name"),
      value: `${user?.tenant?.name || ""} ${user?.tenant?.surname || ""}`,
    },
    {
      label: t("profile.dateofbirth"),
      value: user?.tenant?.birthDate
        ? moment(user.tenant.birthDate).format("DD-MM-YYYY")
        : "",
    },
    {
      label: t("profile.shared_income"),
      value: formatCurrency(user?.tenant?.sharedIncome),
    },
  ];

  const contactDetails = [
    { label: t("profile.email"), value: user?.contactDetailsDTO?.email || "" },
    { label: t("profile.phone"), value: user?.tenant?.phoneNumber || "" },
  ];

  useEffect(() => {
    document.title = `${t("profile.profile")} - GoHome.rent`;
  }, []);

  const renderList = (items: any) =>
    items.map(
      (item: any, index: number) =>
        item.value && (
          <ListItem divider key={index}>
            <ListItemText
              primary={
                <Typography variant="body2" color="grey">
                  {item.label}
                </Typography>
              }
              secondary={<Typography>{item.value}</Typography>}
            />
          </ListItem>
        )
    );

  return (
    <>
      <Modal
        headerCloseButton={true}
        actions={false}
        open={editModal}
        setOpen={() => setEditModal(!editModal)}
        title={t("profile.edit_profile")}
        size={"md"}
        onClose={closeEditModal}
      >
        <ProfileOverviewEditForm
          onClose={closeEditModal}
          reloadUserData={reloadUserData}
        />
      </Modal>

      {user && (
        <Container maxWidth="xl">
          <Grid container padding={isSmallScreen ? 1 : 3} spacing={1}>
            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"space-between"}>
                <Box
                  display={"flex"}
                  alignContent={"center"}
                  gap={1}
                  alignItems={"center"}
                >
                  <Box>
                    <AccountCircleIcon
                      sx={{
                        width: 40,
                        height: 40,
                      }}
                      color="primary"
                    />
                  </Box>
                  <Typography fontWeight={400} variant="h2">
                    {user?.tenant?.name}{" "}
                    {user?.tenant?.surname === null
                      ? ""
                      : user?.tenant?.surname}
                  </Typography>
                </Box>
                <Chip
                  size={isSmallScreen ? "small" : "medium"}
                  variant="filled"
                  sx={{
                    color: theme.palette.primary.main,
                    bacgroundColor: theme.palette.primary.light,
                  }}
                  label={
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      flexDirection={"row"}
                      gap={1}
                    >
                      {user.isPremium && <StarsIcon />}
                      <Typography variant="body2" fontWeight={"bold"}>
                        {user.isPremium
                          ? t("profile.premium_account")
                          : t("profile.basic_account")}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <List dense>{renderList(profileInfo)}</List>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <List dense>{renderList(contactDetails)}</List>
            </Grid>
            <Grid item xs={12}>
              {!isSmallScreen && (
                <Box
                  marginTop={2}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  gap={2}
                >
                  <Box display={"flex"} gap={2}>
                    <Button
                      onClick={() => setEditModal(!editModal)}
                      startIcon={<EditIcon />}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {t("profile.edit_profile")}
                    </Button>
                    {!user.isPremium && (
                      <Button
                        onClick={() =>
                          navigate(
                            "/" + NAV.PROFILE + "/" + NAV.PREMIUM_UPGRADE
                          )
                        }
                        startIcon={<StarIcon />}
                        sx={{
                          backgroundColor: theme.palette.secondary.main,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.secondary.main,
                          },
                        }}
                      >
                        {t("profile.upgrade")}
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
              {isSmallScreen && (
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <Button
                    size="small"
                    onClick={() => setEditModal(!editModal)}
                    startIcon={<EditIcon />}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    {t("profile.edit_profile")}
                  </Button>

                  {!user.isPremium && (
                    <Button
                      size="small"
                      startIcon={<StarIcon />}
                      sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: theme.palette.secondary.light,
                          color: theme.palette.secondary.main,
                        },
                      }}
                    >
                      <Link
                        sx={{
                          color: "#fff",
                        }}
                        href={"/" + NAV.PREMIUM_UPGRADE}
                      >
                        {t("profile.upgrade")}
                      </Link>
                    </Button>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
};

export default ProfileOverview;
